import React, {useState, memo} from "react";
import {useDispatch, useSelector} from "react-redux";

import {useAppKit, useAppKitProvider, useDisconnect} from "@reown/appkit/react";
import {useTranslation} from "react-i18next";
import {ethers} from "ethers";

import {useAuthorization, useBalance, useConnectedWallet, useGMTEvents, useLocalStorage} from "hooks";
import {addressReduction, ERROR, errorHandler, GLOBAL_MODALS, PROCESS, SUCCESS, wait} from "utils";
import {authV2Service} from "store/AuthV2/AuthV2Service";
import {globalService} from "store/Global/globalService";
import {setData} from "store/AuthV2/AuthV2Slice";
import {useGlobalModalsContext} from "layouts";

import ProfileInfoHeader from "./ProfileInfoHeader";
import ProfileInfoBlock from "./ProfileInfoBlock";
import {ButtonV2} from "components/atoms";

const ProfileWalletBlock = () => {
    const dispatch = useDispatch()

    const {isConnected, address, connectedChainId} = useConnectedWallet()
    const {setModal, setMessage, resetStatesAndClose} = useGlobalModalsContext()
    const {removeSyncDate, removeWalletCheckDate} = useLocalStorage()
    const {getKMTBalanceAndSave, getBalanceAndSave} = useBalance()
    const {walletProvider} = useAppKitProvider("eip155")
    const {accessToken} = useAuthorization()
    const {updateWallet} = useGMTEvents()
    const {t} = useTranslation()
    const {disconnect} = useDisconnect()
    const {open} = useAppKit()

    const [processMessage, setProcessMessage] = useState("")

    const {userData} = useSelector(state => state.authV2.signIn)

    const resetProcessInfo = () => {
        setTimeout(() => setProcessMessage(""), 5000)
    }

    const walletMutationHandler = async () => {
        setModal(GLOBAL_MODALS.PROCESS)

        const userWalletProvider = new ethers.providers.Web3Provider(walletProvider)
        const walletConnectSigner = userWalletProvider.getSigner()

        await walletConnectSigner.signMessage(PROCESS.WALLET_CONFIRM)
            .then(async (signature) => {
                const signerAddress = ethers.utils.verifyMessage(PROCESS.WALLET_CONFIRM, signature);

                if (userData.wallet) {
                    if (signerAddress.toLowerCase() === userData.wallet.toLowerCase()) {
                        await authV2Service.removeWallet()

                        await wait(4000)
                        dispatch(setData({
                            path1: "signIn",
                            path2: "userData",
                            data: {...userData, wallet: null, kompeteBalance: 0, ethBalance: 0}
                        }))

                        setModal(GLOBAL_MODALS.SUCCESS)
                        setMessage(SUCCESS.WALLET_REMOVED)
                        resetStatesAndClose()

                        await disconnect()
                        updateWallet(userData.id)
                        removeWalletCheckDate()
                        resetProcessInfo()
                        removeSyncDate()

                        globalService.checkSyncDateAndSendInventories()
                    } else {
                        errorHandler(ERROR.INVALID_WALLET)
                    }
                } else {
                    if (signerAddress.toLowerCase() === address.toLowerCase()) {
                        await authV2Service.addWallet(address, accessToken)

                        await wait(1000)
                        dispatch(setData({
                            path1: "signIn",
                            path2: "userData",
                            data: {...userData, wallet: address.toLowerCase()}
                        }))

                        setModal(GLOBAL_MODALS.SUCCESS)
                        setMessage(SUCCESS.WALLET_ADDED)
                        resetStatesAndClose()

                        updateWallet(userData.id)
                        resetProcessInfo()
                        removeSyncDate()

                        globalService.checkSyncDateAndSendInventories()
                        getBalances()
                    } else {
                        errorHandler(ERROR.INVALID_WALLET)
                    }
                }
            })
            .catch((error) => {
                setModal(GLOBAL_MODALS.ERROR)
                setMessage(error?.message ?? ERROR.FAIL_REQUEST)
                resetStatesAndClose()
            })
    }

    const getBalances = async () => {
        await getKMTBalanceAndSave(connectedChainId, address)
        await getBalanceAndSave(connectedChainId, address)
    }

    const infoText = userData.wallet ? addressReduction(userData.wallet, 6) : t("profilePageNoWallet")
    const buttonText =
        isConnected ?
            userData.wallet
                ? t("profilePageRemoveWalletButtonText")
                : t("profilePageAddWalletButtonText")
            : t("connectWalletButtonText")

    const buttonOnclick = () => isConnected ? walletMutationHandler() : open()

    return (
        <div className="profile_container_info_wallet">
            <ProfileInfoHeader title={t("wallet")} processInfo={processMessage}/>
            <ProfileInfoBlock text={infoText}/>
            <p className="profile_container_info_wallet_note">
                {t("profilePageWalletInfo")}
            </p>
            <ButtonV2 onClick={buttonOnclick}>{buttonText}</ButtonV2>
        </div>
    )
}

export default memo(ProfileWalletBlock)